import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../lib/api'

interface Payload {
  module: {
    id: string
    slug: string
    floor: number
    title: string
    zone: string
    boss: string
    summary: string
    color: string
  }
  lessons: { id: string; slug: string; position: number; title: string; est_minutes: number; xp: number }[]
  completed: string[]
}

export default function ModuleView() {
  const { slug } = useParams()
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    api<Payload>(`/modules/${slug}`, { signal: controller.signal })
      .then(setData)
      .catch((e: unknown) => {
        if ((e as Error).name !== 'AbortError') setError((e as Error).message)
      })
    return () => controller.abort()
  }, [slug])

  if (error) return <div className="error-panel">{error}</div>
  if (!data) return <div className="loading">Loading floor…</div>

  const done = new Set(data.completed)
  const allDone = data.lessons.length > 0 && data.lessons.every((l) => done.has(l.id))

  return (
    <div className="module-view">
      <nav className="breadcrumb">
        <Link to="/">Tower</Link>
        <span>/</span>
        <span>Floor {data.module.floor}</span>
      </nav>

      <header className="module-header" style={{ borderColor: data.module.color }}>
        <p className="dim">Floor {data.module.floor}</p>
        <h1>{data.module.zone}</h1>
        <p className="module-subject">{data.module.title}</p>
        <p>{data.module.summary}</p>
      </header>

      <ol className="lesson-list">
        {data.lessons.map((l) => (
          <li key={l.id} className={done.has(l.id) ? 'lesson-item lesson-item-done' : 'lesson-item'}>
            <Link to={`/floor/${data.module.slug}/${l.slug}`}>
              <span className="lesson-index">{l.position}</span>
              <span className="lesson-title">{l.title}</span>
              <span className="dim">
                {done.has(l.id) ? '✓' : `${l.est_minutes} min`}
              </span>
            </Link>
          </li>
        ))}
      </ol>

      <section className={allDone ? 'boss-card boss-ready' : 'boss-card'}>
        <h3>⚔ Floor boss — {data.module.boss}</h3>
        {allDone ? (
          <>
            <p>
              Every lesson on this floor is done. Eight questions, ten minutes, 100 HP — and the
              combo multiplier is the only way to bring it down.
            </p>
            <Link to={`/floor/${data.module.slug}/boss`} className="button button-primary">
              Challenge {data.module.boss}
            </Link>
          </>
        ) : (
          <p className="dim">
            Finish every lesson on this floor to challenge {data.module.boss} —{' '}
            {data.lessons.filter((l) => done.has(l.id)).length} of {data.lessons.length} done.
          </p>
        )}
      </section>
    </div>
  )
}
